<script lang="ts">
  import type { BudgetSuggestion } from '../../lib/budget/suggestions';
  import SuggestionCard from '../common/SuggestionCard.svelte';

  let {
    suggestion,
    selected = false,
    disabled = false,
    onToggleSelect,
    onAccept,
    onCustomize,
    onDismiss
  }: {
    suggestion: BudgetSuggestion;
    selected?: boolean;
    disabled?: boolean;
    onToggleSelect: () => void;
    onAccept: (suggestion: BudgetSuggestion) => void;
    onCustomize: (suggestion: BudgetSuggestion) => void;
    onDismiss: (suggestion: BudgetSuggestion) => void;
  } = $props();

  let isEditing = $state(false);
  let editedAmount = $state(0);
  let inputElement: HTMLInputElement | undefined = $state(undefined);

  // Initialize editedAmount from suggestion
  $effect(() => {
    editedAmount = suggestion.suggestedAmount;
  });

  function getConfidenceVariant(confidence: number): 'success' | 'warning' | 'default' {
    if (confidence >= 0.8) return 'success';
    if (confidence >= 0.6) return 'warning';
    return 'default';
  }

  function getConfidenceLabel(confidence: number): string {
    if (confidence >= 0.8) return 'High Confidence';
    if (confidence >= 0.6) return 'Medium Confidence';
    return 'Low Confidence';
  }

  function getBudgetTypeLabel(type: string): string {
    return type.charAt(0).toUpperCase() + type.slice(1);
  }

  function formatCurrency(amount: number): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  }

  function handleAmountClick() {
    isEditing = true;
    // Focus input after it renders
    setTimeout(() => inputElement?.focus(), 0);
  }

  function handleAmountBlur() {
    isEditing = false;
    // Update the suggestion's amount
    suggestion.suggestedAmount = editedAmount;
  }

  function handleAmountKeydown(e: KeyboardEvent) {
    if (e.key === 'Enter') {
      isEditing = false;
      suggestion.suggestedAmount = editedAmount;
    } else if (e.key === 'Escape') {
      isEditing = false;
      editedAmount = suggestion.suggestedAmount; // Revert changes
    }
  }

  function handleAmountInput(e: Event) {
    const target = e.target as HTMLInputElement;
    const value = parseFloat(target.value);
    if (!isNaN(value) && value >= 0) {
      editedAmount = value;
    }
  }

  const badges = [
    { label: getBudgetTypeLabel(suggestion.type), variant: 'primary' as const },
    { label: getConfidenceLabel(suggestion.confidence), variant: getConfidenceVariant(suggestion.confidence) },
    { label: 'Suggested', variant: 'info' as const }
  ];
</script>

<SuggestionCard
  title={suggestion.name}
  {badges}
  description={suggestion.reasoning}
  {selected}
  {disabled}
  showCheckbox={true}
  showDetailsToggle={true}
  {onToggleSelect}
>
  {#snippet rightContent()}
    <div class="suggestion-amount">
      {#if isEditing}
        <div class="amount-edit">
          <span class="currency-symbol">$</span>
          <input
            type="number"
            bind:this={inputElement}
            value={editedAmount}
            oninput={handleAmountInput}
            onblur={handleAmountBlur}
            onkeydown={handleAmountKeydown}
            min="0"
            step="1"
            class="amount-input"
          />
        </div>
      {:else}
        <button
          class="amount-value"
          onclick={handleAmountClick}
          type="button"
          title="Click to adjust amount"
        >
          {formatCurrency(editedAmount)}
        </button>
      {/if}
      {#if suggestion.percentOfIncome}
        <div class="percent-of-income">{suggestion.percentOfIncome}% of income</div>
      {/if}
    </div>
  {/snippet}

  {#snippet children()}
    <div class="statistics">
      <div class="stat-item">
        <span class="stat-label">Average:</span>
        <span class="stat-value">{formatCurrency(suggestion.statistics.average)}</span>
      </div>
      <div class="stat-item">
        <span class="stat-label">Median:</span>
        <span class="stat-value">{formatCurrency(suggestion.statistics.median)}</span>
      </div>
      <div class="stat-item">
        <span class="stat-label">Range:</span>
        <span class="stat-value">
          {formatCurrency(suggestion.statistics.min)} - {formatCurrency(suggestion.statistics.max)}
        </span>
      </div>
      <div class="stat-item">
        <span class="stat-label">Transactions:</span>
        <span class="stat-value">{suggestion.statistics.transactionCount}</span>
      </div>
    </div>
  {/snippet}
</SuggestionCard>

<style lang="scss">
  .statistics {
    background: var(--color-background-tertiary);
    border: 1px solid var(--color-border-light);
    border-radius: var(--radius-md);
    padding: var(--spacing-md);
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: var(--spacing-sm);
  }

  .stat-item {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .stat-label {
    font-size: var(--font-size-xs);
    color: var(--color-text-tertiary);
  }

  .stat-value {
    font-size: var(--font-size-sm);
    color: var(--color-text-primary);
    font-weight: var(--font-weight-medium);
  }

  .suggestion-amount {
    text-align: right;
  }

  .amount-value {
    font-size: var(--font-size-xl);
    font-weight: var(--font-weight-bold);
    color: var(--color-text-primary);
    line-height: 1;
    margin-bottom: 2px;
    background: var(--color-background);
    border: 1px dashed var(--color-border-medium);
    padding: var(--spacing-xs) var(--spacing-sm);
    border-radius: var(--radius-md);
    cursor: pointer;
    transition: all var(--transition-fast);
    position: relative;

    &:hover {
      background: var(--color-background-tertiary);
      border-style: solid;
      border-color: var(--color-primary);
      color: var(--color-primary);
    }

    &:active {
      transform: scale(0.98);
    }
  }

  .amount-edit {
    display: flex;
    align-items: center;
    gap: var(--spacing-xs);
    background: var(--color-background);
    border: 2px solid var(--color-primary);
    border-radius: var(--radius-md);
    padding: var(--spacing-xs) var(--spacing-sm);
    box-shadow: var(--shadow-focus-ring);
  }

  .currency-symbol {
    font-size: var(--font-size-xl);
    font-weight: var(--font-weight-bold);
    color: var(--color-text-tertiary);
  }

  .amount-input {
    font-size: var(--font-size-xl);
    font-weight: var(--font-weight-bold);
    color: var(--color-text-primary);
    border: none;
    outline: none;
    background: none;
    width: 120px;
    padding: 0;

    /* Remove number input spinners */
    &::-webkit-inner-spin-button,
    &::-webkit-outer-spin-button {
      -webkit-appearance: none;
      appearance: none;
      margin: 0;
    }

    -moz-appearance: textfield;
    appearance: textfield;
  }

  .percent-of-income {
    font-size: var(--font-size-xxs);
    color: var(--color-text-tertiary);
  }

  @media (max-width: 1024px) {
    .suggestion-amount {
      text-align: left;
    }
  }

  @media (max-width: 640px) {
    .statistics {
      grid-template-columns: 1fr;
    }
  }
</style>
